import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Send, X, AlertTriangle, ChevronDown, Wallet, ExternalLink, Edit, Trash2, Download, Copy, Check, AlertOctagon, Key, Activity, Settings, RefreshCw, Plus } from 'lucide-react';
import { TradingWallet, restoreTradingWalletKeypair } from '../../services/tradingWalletService';
import { getBalance, getTokenBalances } from '../../services/shyftService';
import { instantTransferSOL, instantTransferToken } from '../../services/instantWalletTransferService';
import { useWalletBalances } from '../../services/walletBalanceService';
import { getPrivateKeyBackup, deleteTradingWallet } from '../../services/tradingWalletService';
import { getTokenInfoByMint, getTokenInfoBySymbol } from '../../services/tokenRegistry';
import { sendSol, sendToken as sendTokenTransaction } from '../../services/tokenTransferService';
import { notifyTradingWalletDeleted } from '../WalletConnection';
import { 
  hasWalletKeyPassword, 
  verifyWalletKeyPassword, 
  setWalletKeyPassword 
} from '../../services/userProfileService';
import { decryptPrivateKeyWithPassword } from '../../utils/walletCryptoUtils';
import { getWalletTokensWithMetadata } from '../../services/solanatrackerService';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import GlobalDropdown from '../GlobalDropdown';
import NumberInput from '../ui/NumberInput';
import { getUserProfile } from '../../services/userProfileService';
import { useWalletUser } from '../../hooks/useWalletUser';

// Import Solana web3.js components
import { 
  PublicKey, 
  SystemProgram, 
  Transaction, 
  LAMPORTS_PER_SOL,
  Connection
} from '@solana/web3.js';

// Import SPL Token components for token transfers
import {
  createTransferInstruction,
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID
} from '@solana/spl-token';

// Use the Solana mainnet RPC URL from environment variable
const RPC_URL = import.meta.env.VITE_SOLANA_RPC_URL;
const connection = new Connection(RPC_URL, 'confirmed');

// Extend Window interface to include our custom property
declare global {
  interface Window {
    ekkoLastTransactionUpdate?: number;
  }
}

// Constants
const GAS_RESERVE_SOL = 0.001; // Reserve 0.001 SOL for gas fees

// Extend the TradingWallet interface to include last_updated
interface ExtendedTradingWallet extends TradingWallet {
  last_updated?: string; // Optional timestamp used for balance update tracking
}

interface AvailableToken {
  mint: string;
  symbol: string;
  name: string;
  balance: string;
  image?: string;
}

interface TokenBalance {
  mint: string;
  symbol: string;
  name?: string;
  image?: string;
  balance: string;
  value?: number;
  decimals?: number;
}

interface WalletData extends ExtendedTradingWallet {
  solBalance?: number;
  tokens?: TokenBalance[];
  totalValue?: number;
  lastUpdated?: number;
}

interface InstantWalletTransferProps {
  wallets: ExtendedTradingWallet[];
  userWalletAddress: string;
  sourceWalletId?: string;
  onClose: () => void;
  onSuccess: () => void;
  onWalletUpdate?: (updatedWallet: ExtendedTradingWallet) => void;
  initialTab?: 'transfer' | 'manage';
}

const InstantWalletTransfer: React.FC<InstantWalletTransferProps> = ({
  wallets,
  userWalletAddress,
  sourceWalletId,
  onClose,
  onSuccess,
  onWalletUpdate,
  initialTab = 'transfer'
}) => {
  // Core state
  const [activeTab, setActiveTab] = useState<'transfer' | 'manage'>(initialTab);
  const [walletsData, setWalletsData] = useState<WalletData[]>([]);
  const [mainWalletData, setMainWalletData] = useState<WalletData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Transfer state
  const [fromWallet, setFromWallet] = useState<string>('main');
  const [toWallet, setToWallet] = useState<string>('');
  const [selectedToken, setSelectedToken] = useState<string>('native');
  const [amount, setAmount] = useState<string>('');
  const [recipientAddress, setRecipientAddress] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [txSignature, setTxSignature] = useState<string | null>(null);
  
  // Management state
  const [selectedWalletForManagement, setSelectedWalletForManagement] = useState<string>('');
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [privateKeyBackup, setPrivateKeyBackup] = useState('');
  const [isExportingKey, setIsExportingKey] = useState(false);
  const [isDeletingWallet, setIsDeletingWallet] = useState(false);
  
  // Password protection state
  const [hasPassword, setHasPassword] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStep, setPasswordStep] = useState<'verify' | 'export'>('verify');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Utility functions
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (balance: number, decimals = 4) => {
    if (balance === 0) return '0';
    if (balance < 0.0001) return balance.toFixed(8);
    return balance.toFixed(decimals);
  };

  const formatUSD = (value: number) => {
    if (value === 0) return '$0.00';
    if (value < 0.01) return '<$0.01';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Fetch wallet data using SolanaTracker
  const fetchWalletData = useCallback(async (wallet: ExtendedTradingWallet): Promise<WalletData> => {
    try {
      // Fetch SOL balance
      const lamports = await getBalance(wallet.public_key);
      const solBalance = lamports / LAMPORTS_PER_SOL;
      
      // Fetch token metadata from SolanaTracker
      const response = await getWalletTokensWithMetadata(wallet.public_key);
      
      let tokens: TokenBalance[] = [];
      let totalValue = solBalance * 200; // Approximate SOL price as fallback
      
      if (response.status === 'success' && response.data?.tokens) {
        tokens = response.data.tokens
          .filter((token: any) => parseFloat(token.balance) > 0)
          .map((token: any) => ({
            mint: token.mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || token.symbol || 'Unknown',
            balance: token.balance,
            image: token.image,
            value: token.value || 0,
            decimals: token.decimals || 9
          }));

        // Calculate total value from API data
        totalValue = tokens.reduce((sum, token) => sum + (token.value || 0), 0);
      }

      return {
        ...wallet,
        solBalance,
        tokens,
        totalValue,
        lastUpdated: Date.now()
      };
    } catch (error) {
      console.error(`Error fetching data for wallet ${wallet.public_key}:`, error);
      return {
        ...wallet,
        solBalance: 0,
        tokens: [],
        totalValue: 0,
        lastUpdated: Date.now()
      };
    }
  }, []);

  // Fetch main wallet data
  const fetchMainWalletData = useCallback(async () => {
    if (!userWalletAddress) return;
    
    try {
      const lamports = await getBalance(userWalletAddress);
      const solBalance = lamports / LAMPORTS_PER_SOL;

      const response = await getWalletTokensWithMetadata(userWalletAddress);
      
      let tokens: TokenBalance[] = [];
      let totalValue = solBalance * 200;

      if (response.status === 'success' && response.data?.tokens) {
        tokens = response.data.tokens
          .filter((token: any) => parseFloat(token.balance) > 0)
          .map((token: any) => ({
            mint: token.mint,
            symbol: token.symbol || 'Unknown',
            name: token.name || token.symbol || 'Unknown',
            balance: token.balance,
            image: token.image,
            value: token.value || 0,
            decimals: token.decimals || 9
          }));
        
        totalValue = tokens.reduce((sum, token) => sum + (token.value || 0), 0);
      }

      setMainWalletData({
        id: 'main',
        name: 'Main Wallet',
        public_key: userWalletAddress,
        created_at: '',
        owner_wallet_address: userWalletAddress,
        is_active: true,
        solBalance,
        tokens,
        totalValue,
        lastUpdated: Date.now()
      });
    } catch (error) {
      console.error('Error fetching main wallet data:', error);
    }
  }, [userWalletAddress]);
  
  // Load all wallets data
  const loadWalletsData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
      
      try {
      // Fetch data for all trading wallets in parallel
      const walletsWithData = await Promise.all(
        wallets.map(wallet => fetchWalletData(wallet))
      );

      setWalletsData(walletsWithData);

      // Also fetch main wallet data
      await fetchMainWalletData();
    } catch (err) {
      console.error('Error loading wallets data:', err);
      setError('Failed to load wallet data');
    } finally {
      setIsLoading(false);
    }
  }, [wallets, fetchWalletData, fetchMainWalletData]);
  
  // Load data on mount
  useEffect(() => {
    loadWalletsData();
  }, [loadWalletsData]);
  
  // Initialize source wallet if provided
  useEffect(() => {
    if (sourceWalletId) {
      setFromWallet(sourceWalletId);
    }
  }, [sourceWalletId]);
  
  // Get wallet options for dropdowns
  const getWalletOptions = () => {
    const options = [
      { value: 'main', label: 'Main Wallet', data: mainWalletData }
    ];
    
    walletsData.forEach(wallet => {
      options.push({
        value: wallet.public_key,
        label: wallet.name,
        data: wallet
      });
    });
    
    return options;
  };
  
  // Get available tokens for selected wallet
  const getAvailableTokens = (): AvailableToken[] => {
    const walletData = fromWallet === 'main' ? mainWalletData : walletsData.find(w => w.public_key === fromWallet);
    if (!walletData) return [];

    const tokens: AvailableToken[] = [
      {
        mint: 'native',
        symbol: 'SOL',
        name: 'Solana',
        balance: walletData.solBalance?.toString() || '0',
        image: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png'
      }
    ];

    if (walletData.tokens) {
      tokens.push(...walletData.tokens.map(token => ({
        mint: token.mint,
        symbol: token.symbol,
        name: token.name || token.symbol,
        balance: token.balance,
        ...(token.image && { image: token.image })
      })));
    }

    return tokens;
  };
  
  // Handle transfer submission
  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromWallet || !toWallet || !amount) return;
      
    setIsTransferring(true);
    setError(null);
    setSuccessMessage(null);
      
    try {
      const amountNumber = parseFloat(amount);
      if (isNaN(amountNumber) || amountNumber <= 0) {
        throw new Error('Invalid amount');
      }

      let signature: string;
      
      // Determine destination address
      let destinationAddress: string;
      if (toWallet === 'external') {
        if (!recipientAddress) {
          throw new Error('Recipient address is required');
        }
        destinationAddress = recipientAddress;
      } else if (toWallet === 'main') {
        destinationAddress = userWalletAddress;
      } else {
        // It's a trading wallet
        destinationAddress = toWallet;
      }

      // Check if we're transferring from main wallet or trading wallet
      if (fromWallet === 'main') {
        // Transfer from main wallet using connected wallet services
        if (selectedToken === 'native') {
          const result = await sendSol(destinationAddress, amountNumber);
          if (!result.success) {
            throw new Error(result.error || 'SOL transfer failed');
          }
          signature = result.signature!;
        } else {
          // Get token info for transfer
          const availableTokens = getAvailableTokens();
          const tokenData = availableTokens.find(t => t.mint === selectedToken);
          if (!tokenData) {
            throw new Error('Selected token not found');
          }

          // Get decimals from the wallet data if available, default to 9
          let decimals = 9;
          const walletData = mainWalletData;
          if (walletData && walletData.tokens) {
            const tokenInfo = walletData.tokens.find(t => t.mint === selectedToken);
            if (tokenInfo && typeof tokenInfo.decimals === 'number') {
              decimals = tokenInfo.decimals;
            }
          }

          const result = await sendTokenTransaction(destinationAddress, amountNumber, selectedToken, decimals);
          if (!result.success) {
            throw new Error(result.error || 'Token transfer failed');
          }
          signature = result.signature!;
        }
      } else {
        // Transfer from trading wallet using instant transfer services
        if (selectedToken === 'native') {
          const result = await instantTransferSOL(
            userWalletAddress,
            fromWallet,
            destinationAddress,
            amountNumber
          );
          if (!result.success) {
            throw new Error(result.error || 'SOL transfer failed');
          }
          signature = result.signature!;
        } else {
          // Get token info for transfer
          const availableTokens = getAvailableTokens();
          const tokenData = availableTokens.find(t => t.mint === selectedToken);
          if (!tokenData) {
            throw new Error('Selected token not found');
          }

          // Get decimals from the wallet data if available, default to 9
          let decimals = 9;
          const walletData = walletsData.find(w => w.public_key === fromWallet);
          if (walletData && walletData.tokens) {
            const tokenInfo = walletData.tokens.find(t => t.mint === selectedToken);
            if (tokenInfo && typeof tokenInfo.decimals === 'number') {
              decimals = tokenInfo.decimals;
            }
          }

          const result = await instantTransferToken(
            userWalletAddress,
            fromWallet,
            destinationAddress,
            amountNumber,
            selectedToken,
            decimals
          );
          if (!result.success) {
            throw new Error(result.error || 'Token transfer failed');
          }
          signature = result.signature!;
        }
      }
      
      setSuccessMessage('Transfer completed successfully!');
      setTxSignature(signature);
        
      // Refresh wallet data
      await loadWalletsData();
      
      // Reset form
      setAmount('');
      setRecipientAddress('');
        
      // Call success callback
      onSuccess();

      console.log(`✅ Transfer successful: ${amount} ${selectedToken === 'native' ? 'SOL' : 'Token'} from ${fromWallet === 'main' ? 'main wallet' : 'trading wallet'} to ${toWallet === 'external' ? 'external wallet' : 'trading wallet'}`);

      // COMPREHENSIVE BALANCE REFRESH SYSTEM
      console.log('🔄 Starting comprehensive balance refresh...');
      
      // Get wallet user for effective wallet address
      const walletUser = useWalletUser();
      const effectiveWalletAddress = walletUser.walletAddress;
      
      // Get token symbol for display
      const tokenSymbol = selectedToken === 'native' ? 'SOL' : (getAvailableTokens().find((t: any) => t.mint === selectedToken)?.symbol || 'Unknown');
      
      // 1. Dispatch transfer completion event (new global event)
      window.dispatchEvent(new CustomEvent('ekko:transfer_completed', {
        detail: {
          fromWallet: fromWallet === 'main' ? mainWalletData?.public_key : fromWallet,
          toWallet: toWallet === 'external' ? recipientAddress : toWallet,
          amount: parseFloat(amount),
          tokenMint: selectedToken === 'native' ? 'SOL' : selectedToken,
          tokenSymbol: tokenSymbol,
          transferType: selectedToken === 'native' ? 'sol' : 'token',
          timestamp: Date.now(),
          signature: signature
        }
      }));

      // 2. Force refresh source wallet (from)
      const sourceWalletAddress = fromWallet === 'main' ? mainWalletData?.public_key : fromWallet;
      if (sourceWalletAddress) {
        console.log(`🔄 Refreshing source wallet: ${sourceWalletAddress.slice(0, 8)}...`);
        
        // Dispatch wallet_selected event for source wallet
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
          detail: { walletPublicKey: sourceWalletAddress }
        }));
        
        // Dispatch specific balance update events for source wallet
        if (selectedToken === 'native') {
          window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
            detail: {
              address: sourceWalletAddress,
              balance: null, // Force refresh
              forceRefresh: true
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
            detail: {
              address: sourceWalletAddress,
              mint: selectedToken,
              balance: null, // Force refresh
              forceRefresh: true
            }
          }));
        }
      }

      // 3. Force refresh destination wallet (to) - only if it's not external
      if (toWallet !== 'external') {
        console.log(`🔄 Refreshing destination wallet: ${toWallet.slice(0, 8)}...`);
        
        // Dispatch wallet_selected event for destination wallet
        window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
          detail: { walletPublicKey: toWallet }
        }));
        
        // Dispatch specific balance update events for destination wallet
        if (selectedToken === 'native') {
          window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
            detail: {
              address: toWallet,
              balance: null, // Force refresh
              forceRefresh: true
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
            detail: {
              address: toWallet,
              mint: selectedToken,
              balance: null, // Force refresh
              forceRefresh: true
            }
          }));
        }
      }

      // 4. Force global balance cache invalidation
      window.dispatchEvent(new CustomEvent('ekko:invalidate_balance_cache', {
        detail: {
          wallets: [sourceWalletAddress, toWallet !== 'external' ? toWallet : null].filter(Boolean),
          timestamp: Date.now()
        }
      }));

      // 5. Dispatch trade_completed event for compatibility with existing systems
      window.dispatchEvent(new CustomEvent('ekko:trade_completed', {
        detail: {
          type: 'transfer',
          amount: amount,
          walletAddress: sourceWalletAddress,
          destinationWallet: toWallet === 'external' ? recipientAddress : toWallet,
          transferType: selectedToken === 'native' ? 'sol' : 'token',
          timestamp: Date.now()
        }
      }));

      // 6. Trigger active wallet refresh if either wallet is the active trading wallet
      if (effectiveWalletAddress) {
        try {
          const profile = await getUserProfile(effectiveWalletAddress);
          if (profile?.active_trade_wallet) {
            if (sourceWalletAddress === profile.active_trade_wallet || toWallet === profile.active_trade_wallet) {
              console.log(`🔄 Refreshing active trading wallet: ${profile.active_trade_wallet.slice(0, 8)}...`);
              window.dispatchEvent(new CustomEvent('ekko:active_wallet_changed', {
                detail: {
                  previousWallet: profile.active_trade_wallet,
                  currentWallet: profile.active_trade_wallet, // Same wallet, just refresh
                  forceRefresh: true
                }
              }));
            }
          }
        } catch (profileError) {
          console.error('Error getting user profile for active wallet refresh:', profileError);
        }
      }

      // 7. Schedule delayed refreshes to catch any stragglers
      setTimeout(() => {
        console.log('🔄 Delayed refresh (2s)...');
        if (sourceWalletAddress) {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: sourceWalletAddress }
          }));
        }
        if (toWallet !== 'external') {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: toWallet }
          }));
        }
      }, 2000);

      setTimeout(() => {
        console.log('🔄 Final refresh (5s)...');
        if (sourceWalletAddress) {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: sourceWalletAddress }
          }));
        }
        if (toWallet !== 'external') {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: toWallet }
          }));
        }
      }, 5000);

      console.log('✅ Comprehensive balance refresh events dispatched');
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err instanceof Error ? err.message : 'Transfer failed');
    } finally {
      setIsTransferring(false);
    }
  };

  // Handle private key export
  const handleExportPrivateKey = async () => {
    if (!selectedWalletForManagement) return;

    setIsExportingKey(true);
    try {
      // Check if user has password protection enabled
      const hasPass = await hasWalletKeyPassword(userWalletAddress);
      setHasPassword(hasPass);
      
      if (hasPass) {
        setPasswordStep('verify');
        } else {
        setPasswordStep('export');
          }
          
      setShowPrivateKeyModal(true);
    } catch (error) {
      console.error('Error checking password status:', error);
      setError('Failed to check password status');
    } finally {
      setIsExportingKey(false);
    }
  };
  
  // Handle password verification
  const handlePasswordVerification = async () => {
    if (!selectedWalletForManagement) return;

    setIsVerifying(true);
    try {
      if (hasPassword) {
        const isValid = await verifyWalletKeyPassword(userWalletAddress, verifyPassword);
        if (!isValid) {
          setError('Invalid password');
          setIsVerifying(false);
          return;
        }
      }
      
      setPasswordStep('export');
      setError(null);
        } catch (error) {
      console.error('Error verifying password:', error);
      setError('Failed to verify password');
        } finally {
      setIsVerifying(false);
      }
    };
  
  // Handle private key export with password
  const handlePrivateKeyExport = async () => {
    if (!selectedWalletForManagement) return;
    
    setIsExportingKey(true);
    try {
      const passwordToUse = hasPassword ? verifyPassword : exportPassword;
      
      if (!hasPassword && exportPassword) {
        await setWalletKeyPassword(userWalletAddress, exportPassword);
      }

      // Password is only for access control, always get raw backup format
      const backup = await getPrivateKeyBackup(
        userWalletAddress,
        selectedWalletForManagement
      );

      setPrivateKeyBackup(backup);
    } catch (error) {
      console.error('Error exporting private key:', error);
      setError('Failed to export private key');
    } finally {
      setIsExportingKey(false);
    }
  };
  
  // Handle wallet deletion
  const handleDeleteWallet = async () => {
    if (!selectedWalletForManagement) return;

    setIsDeletingWallet(true);
    try {
      await deleteTradingWallet(userWalletAddress, selectedWalletForManagement);
      
      // Update wallets data
      setWalletsData(prev => prev.filter(w => w.public_key !== selectedWalletForManagement));
      
      // Notify parent
      notifyTradingWalletDeleted();
      onSuccess();
      
      setShowDeleteModal(false);
      setSelectedWalletForManagement('');
    } catch (error) {
      console.error('Error deleting wallet:', error);
      setError('Failed to delete wallet');
    } finally {
      setIsDeletingWallet(false);
    }
  };

  // Render transfer tab
  const renderTransferTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* From Wallet */}
        <div>
          <label className="block text-xs text-gray-400 mb-2">From Wallet</label>
          <GlobalDropdown
            options={getWalletOptions().map(option => ({
              value: option.value,
              label: option.label
            }))}
            value={fromWallet}
            onChange={(value) => setFromWallet(value)}
            placeholder="Select source wallet"
          />
                </div>
                
        {/* To Wallet */}
        <div>
          <label className="block text-xs text-gray-400 mb-2">To Wallet</label>
          <GlobalDropdown
            options={[
              { value: '', label: 'Select destination...' },
              ...getWalletOptions()
                .filter(option => option.value !== fromWallet)
                .map(option => ({
                  value: option.value,
                  label: option.label
                })),
              { value: 'external', label: 'External Address' }
            ]}
            value={toWallet}
            onChange={(value) => setToWallet(value)}
            placeholder="Select destination"
          />
                      </div>
      </div>

      {/* External address input */}
      {toWallet === 'external' && (
        <div>
          <label className="block text-xs text-gray-400 mb-2">Recipient Address</label>
                    <input 
                      type="text"
                      value={recipientAddress}
                      onChange={(e) => setRecipientAddress(e.target.value)}
            placeholder="Enter Solana address..."
            className="w-full bg-gray-600/5 border border-gray-600/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                    />
              </div>
      )}
              
      {/* Token Selection */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Select Token</label>
        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-gray-600/20 rounded-lg p-2">
          {getAvailableTokens().map(token => (
            <button
              key={token.mint}
              type="button"
              onClick={() => setSelectedToken(token.mint)}
              className={`flex items-center justify-between p-2 rounded text-xs transition-all ${
                selectedToken === token.mint
                  ? 'bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b]'
                  : 'bg-gray-600/5 border border-gray-600/20 text-white hover:bg-gray-600/10'
              }`}
            >
              <div className="flex items-center gap-2">
                {token.image && (
                  <img src={token.image} alt={token.symbol} className="w-5 h-5 rounded-full" />
                )}
                <div className="text-left">
                  <div className="font-medium">{token.symbol}</div>
                  <div className="text-gray-400">{token.name}</div>
                  </div>
              </div>
              <div className="text-right">
                <div className="font-medium">{formatBalance(parseFloat(token.balance))}</div>
              </div>
            </button>
          ))}
                </div>
              </div>
              
      {/* Amount Input */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Amount</label>
        <div className="flex gap-2">
                  <NumberInput
                    value={amount}
                    onChange={(value) => setAmount(value)}
                    placeholder="0.00"
                    className="flex-1 bg-gray-600/5 border border-gray-600/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
                  />
                  <button 
                    type="button"
            onClick={() => {
              const tokens = getAvailableTokens();
              const selectedTokenData = tokens.find(t => t.mint === selectedToken);
              if (selectedTokenData) {
                const maxAmount = selectedToken === 'native' 
                  ? Math.max(0, parseFloat(selectedTokenData.balance) - GAS_RESERVE_SOL)
                  : parseFloat(selectedTokenData.balance);
                setAmount(maxAmount.toString());
              }
            }}
            className="px-3 py-2 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
                  >
                    MAX
                  </button>
                </div>
              </div>
              
      {/* Transfer Button */}
              <button
                type="submit"
        disabled={isTransferring || !fromWallet || !toWallet || !amount}
        className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isTransferring ? (
                  <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Transferring...
                  </>
                ) : (
                  <>
            <Send className="w-4 h-4" />
            Transfer
                  </>
                )}
              </button>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-3 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-sm">
          {successMessage}
                  {txSignature && (
            <div className="mt-2">
                      <a
                href={`https://solscan.io/tx/${txSignature}`}
                        target="_blank"
                        rel="noopener noreferrer"
                className="text-xs underline hover:no-underline"
                      >
                        View on Solscan
                      </a>
                    </div>
                  )}
                      </div>
                    )}

      {error && (
        <div className="p-3 rounded bg-red-500/20 border border-red-500/40 text-red-400 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {error}
                    </div>
      )}
                  </div>
  );

  // Render manage tab
  const renderManageTab = () => (
    <div className="space-y-4">
      {/* Wallet Selection */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Select Wallet to Manage</label>
        <GlobalDropdown
          options={[
            { value: '', label: 'Select a trading wallet...' },
            ...walletsData.map(wallet => ({
              value: wallet.public_key,
              label: `${wallet.name} (${formatAddress(wallet.public_key)})`
            }))
          ]}
          value={selectedWalletForManagement}
          onChange={(value) => setSelectedWalletForManagement(value)}
          placeholder="Select wallet to manage"
        />
                      </div>

      {selectedWalletForManagement && (
        <div className="space-y-3">
          {/* Wallet Info */}
          <div className="p-3 rounded bg-gray-600/5 border border-gray-600/20">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-medium">
                {walletsData.find(w => w.public_key === selectedWalletForManagement)?.name}
              </h4>
              <span className="text-xs text-gray-400">
                {formatAddress(selectedWalletForManagement)}
                        </span>
                      </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-400">SOL Balance:</span>
                <div className="text-white font-medium">
                  {formatBalance(walletsData.find(w => w.public_key === selectedWalletForManagement)?.solBalance || 0)} SOL
                  </div>
                      </div>
              <div>
                <span className="text-gray-400">Total Value:</span>
                <div className="text-white font-medium">
                  {formatUSD(walletsData.find(w => w.public_key === selectedWalletForManagement)?.totalValue || 0)}
                </div>
              </div>
                    </div>
                  </div>
                  
          {/* Management Actions */}
          <div className="grid grid-cols-2 gap-3">
                      <button 
              onClick={handleExportPrivateKey}
              disabled={isExportingKey}
              className="py-2 px-3 rounded bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/30 transition-all text-xs flex items-center justify-center gap-2"
            >
              <Key className="w-4 h-4" />
              Export Key
                      </button>
            
                  <button
              onClick={() => setShowDeleteModal(true)}
              className="py-2 px-3 rounded bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-all text-xs flex items-center justify-center gap-2"
                  >
              <Trash2 className="w-4 h-4" />
              Delete Wallet
                  </button>
              </div>
                      </div>
      )}
                    </div>
  );

    return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <style>{globalWidgetTextStyles}</style>
      
      <div className={`bg-white border border-gray-200 rounded-lg w-full max-w-2xl shadow-xl ${widgetClassName}`}>
        {/* Header */}
        <div className="p-2 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-[#24c35b]" />
            <h2 className="text-lg font-bold text-black">Wallet Transfer</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {[
            { key: 'transfer', label: 'Transfer', icon: Send },
            { key: 'manage', label: 'Manage', icon: Settings }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`flex-1 py-1.5 px-2 text-xs font-medium transition-all border-b-2 ${
                activeTab === key
                  ? 'border-[#24c35b] text-[#24c35b] bg-white'
                  : 'border-transparent text-gray-600 hover:text-black hover:bg-gray-100'
              }`}
            >
              <Icon size={14} className="mr-1 inline" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <form onSubmit={handleTransfer} className="p-4 bg-white">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw size={16} className="text-[#24c35b] animate-spin mr-2" />
              <span className="text-gray-600 text-xs">Loading wallet data...</span>
            </div>
          )}

          {/* Tab Content */}
          {!isLoading && (
            <>
              {activeTab === 'transfer' && renderTransferTab()}
              {activeTab === 'manage' && renderManageTab()}
            </>
          )}
        </form>

                {/* Private Key Export Modal */}
        {showPrivateKeyModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-black text-lg font-semibold flex items-center">
                  <Key size={20} className="text-[#24c35b] mr-2" />
                  Export Private Key
                </h3>
                <button
                  onClick={() => {
                    setShowPrivateKeyModal(false);
                    setPrivateKeyBackup('');
                    setExportPassword('');
                    setConfirmPassword('');
                    setVerifyPassword('');
                    setPasswordStep('verify');
                  }}
                  className="text-gray-600 hover:text-black transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

                            <div className="p-4">
                <div className="mb-4 p-3 rounded border border-[#24c35b]/40 bg-[#24c35b]/10">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#24c35b] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#24c35b] font-medium text-sm">Security Warning</p>
                      <p className="text-gray-700 text-xs">
                        Keep your private key secure. Anyone with access can control your wallet.
                      </p>
                    </div>
                  </div>
                </div>
                    
                {!privateKeyBackup ? (
                  <>
                    {passwordStep === 'verify' ? (
                      <div className="space-y-4">
                        {hasPassword ? (
                          <div>
                            <label className="block text-sm font-medium text-black mb-2">
                              Enter your wallet password
                            </label>
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={verifyPassword}
                              onChange={(e) => setVerifyPassword(e.target.value)}
                              className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                              placeholder="Enter your password"
                            />
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-gray-600 text-sm mb-2">
                              No password protection is currently set up.
                            </p>
                            <p className="text-gray-500 text-xs">
                              You can optionally set a password in the next step.
                            </p>
                          </div>
                        )}
                          
                                                  <div className="flex gap-2">
                          <button
                            onClick={() => setShowPrivateKeyModal(false)}
                            className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={hasPassword ? handlePasswordVerification : () => setPasswordStep('export')}
                            disabled={isVerifying || (hasPassword && !verifyPassword)}
                            className="flex-1 py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isVerifying ? 'Verifying...' : 'Continue'}
                          </button>
                        </div>
                        </div>
                    ) : (
                      <div className="space-y-4">
                        {!hasPassword && (
                                                    <>
                            <div>
                              <label className="block text-sm font-medium text-black mb-2">
                                Export Password (Optional)
                              </label>
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={exportPassword}
                                onChange={(e) => setExportPassword(e.target.value)}
                                className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                                placeholder="Enter password to encrypt backup"
                              />
                            </div>
                          
                            {exportPassword && (
                              <div>
                                <label className="block text-sm font-medium text-black mb-2">
                                  Confirm Password
                                </label>
                                <input
                                  type={showPassword ? 'text' : 'password'}
                                  value={confirmPassword}
                                  onChange={(e) => setConfirmPassword(e.target.value)}
                                  className="w-full py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black focus:outline-none focus:border-[#24c35b] focus:ring-2 focus:ring-[#24c35b]/20"
                                  placeholder="Confirm password"
                                />
                              </div>
                            )}
                          </>
                        )}
                          
                                                  <div className="flex gap-2">
                          <button
                            onClick={() => setPasswordStep('verify')}
                            className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
                          >
                            Back
                          </button>
                          <button
                            onClick={handlePrivateKeyExport}
                            disabled={isExportingKey || (exportPassword !== '' && exportPassword !== confirmPassword)}
                            className="flex-1 py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isExportingKey ? 'Exporting...' : 'Export'}
                          </button>
                        </div>
                        </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-black mb-2">
                        Private Key Backup
                      </label>
                      <div className="relative">
                        <textarea
                          value={privateKeyBackup}
                          readOnly
                          className="w-full h-32 py-2 px-3 rounded bg-gray-50 border border-gray-300 text-black font-mono text-xs resize-none"
                        />
                        <button
                          onClick={() => navigator.clipboard.writeText(privateKeyBackup)}
                          className="absolute top-2 right-2 p-1 rounded bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                    </div>
                  </div>
                  
          <button 
            onClick={() => {
                        const blob = new Blob([privateKeyBackup], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `wallet-${selectedWalletForManagement.slice(0, 8)}-private-key.txt`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                      className="w-full py-2 px-4 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Backup File
          </button>

          <button 
                      onClick={() => setShowPrivateKeyModal(false)}
                      className="w-full py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all"
                    >
                      Close
                    </button>
                </div>
              )}
              </div>
            </div>
            </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-xl">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-black text-lg font-semibold flex items-center">
                  <Trash2 size={20} className="text-red-500 mr-2" />
                  Delete Wallet
                </h3>
          <button 
                  onClick={() => setShowDeleteModal(false)}
                  className="text-gray-600 hover:text-black transition-colors"
          >
                  <X className="w-4 h-4" />
          </button>
        </div>
        
              <div className="p-4">
                <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-500 font-medium text-sm">Permanent Action</p>
                      <p className="text-gray-700 text-xs">
                        This will permanently delete the wallet. Make sure you have backed up your private key.
                      </p>
                    </div>
                  </div>
            </div>
            
                <div className="mb-4">
                  <p className="text-gray-600 text-sm mb-1">
                    Wallet: <span className="text-black font-medium">{walletsData.find(w => w.public_key === selectedWalletForManagement)?.name}</span>
                  </p>
                  <p className="text-gray-600 text-sm mb-4">
                    Balance: <span className="text-black font-medium">{formatBalance(walletsData.find(w => w.public_key === selectedWalletForManagement)?.solBalance || 0)} SOL</span>
                  </p>
                </div>

                <div className="flex gap-2">
              <button 
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-600 hover:text-black hover:border-gray-400 transition-all text-sm"
              >
                    Cancel
              </button>
              <button 
                    onClick={handleDeleteWallet}
                    disabled={isDeletingWallet}
                    className="flex-1 py-2 px-4 rounded bg-red-500/20 border border-red-500/40 text-red-500 hover:bg-red-500/30 transition-all text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeletingWallet ? (
                      <>
                        <RefreshCw size={14} className="mr-1 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={14} className="mr-1" />
                        Delete Wallet
                      </>
                    )}
              </button>
            </div>
            </div>
          </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default InstantWalletTransfer;